import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO head component.
 * Renders <title>, meta description/OG/Twitter, canonical and optional JSON-LD.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  locale = "fr",
  altPath, // equivalent path in the other locale (relative, without host)
  noindex = false, // set true for pages we don't want indexed yet (e.g. WIP translations)
  statusCode = null, // e.g. 404 → emits `<meta name="prerender-status-code" content="404" />` honored by Googlebot/Bingbot for soft-404 detection
}) {
  const site = "Bruno Courtin";
  const base = "https://brunocourtin.fr";
  const fullTitle = title ? `${title} — ${site}` : (locale === "en" ? `${site} — French novelist` : `${site} — Auteur français`);
  const defaultDescFr = "Site officiel de Bruno Courtin, auteur français de romans historiques, d'aventure et d'anticipation. Découvrez la trilogie Malo, Les Chaînes du Vivant, Terra Australis, Mauritia et l'univers littéraire complet.";
  const defaultDescEn = "Official website of Bruno Courtin, French novelist. Historical fiction, adventure and anticipation. Discover the Malo trilogy, Les Chaînes du Vivant, Terra Australis, Mauritia and the complete literary universe.";
  const fullDesc = description || (locale === "en" ? defaultDescEn : defaultDescFr);
  const url = `${base}${path}`;
  const ogImage = image?.startsWith("http") ? image : image ? `${base}${image}` : `${base}/og-default.jpg`;
  const frUrl = locale === "fr" ? url : (altPath ? `${base}${altPath}` : base);
  const enUrl = locale === "en" ? url : (altPath ? `${base}${altPath}` : `${base}/en`);

  return (
    <Helmet>
      <html lang={locale} />
      <title>{fullTitle}</title>
      <meta name="description" content={fullDesc} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="fr-FR" href={frUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={frUrl} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-snippet:-1, max-image-preview:large"} />
      {statusCode ? <meta name="prerender-status-code" content={String(statusCode)} /> : null}
      {statusCode ? <meta httpEquiv="Status" content={`${statusCode} Not Found`} /> : null}
      <meta name="author" content="Bruno Courtin" />
      <meta name="language" content={locale === "en" ? "English" : "French"} />

      {/* Open Graph */}
      <meta property="og:site_name" content={site} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={locale === "en" ? "en_GB" : "fr_FR"} />
      <meta property="og:locale:alternate" content={locale === "en" ? "fr_FR" : "en_GB"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDesc} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
